import React from 'react';
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

import InterRegular from '../../../assets/fonts/Inter-Regular.ttf';
import InterBold from '../../../assets/fonts/Inter-Bold.ttf';

Font.register({
  family: 'Inter',
  fonts: [
    { src: InterRegular, fontWeight: 'normal' },
    { src: InterBold, fontWeight: 'bold' },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Inter',
    fontSize: 8,
    color: '#202124',
  },
  heading: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  reportDate: {
    fontSize: 9,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 210,
  },
  logo: {
    width: 34,
    height: 34,
    objectFit: 'contain',
    marginRight: 10,
  },
  businessName: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  parameters: {
    flexDirection: 'row',
    marginTop: 28,
    paddingBottom: 20,
    borderBottom: '0.5px solid #B8B8B8',
  },
  parameterColumn: {
    width: '50%',
  },
  parameterRow: {
    flexDirection: 'row',
    marginBottom: 9,
  },
  parameterLabel: {
    width: 76,
    fontWeight: 'bold',
  },
  parameterValue: {
    flex: 1,
  },
  charts: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  chartCard: {
    width: '49%',
    height: 176,
    border: '0.5px solid #E4E4E4',
    borderRadius: 7,
    padding: 8,
    marginBottom: 8,
  },
  chartCardFull: {
    width: '100%',
  },
  chartTitle: {
    fontSize: 7,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  chartSubtitle: {
    fontSize: 5.5,
    marginBottom: 4,
  },
  chartImage: {
    width: '100%',
    height: 137,
    objectFit: 'contain',
  },
});

const Parameter = ({ label, value }) => (
  <View style={styles.parameterRow}>
    <Text style={styles.parameterLabel}>{label}</Text>
    <Text style={styles.parameterValue}>{value || ''}</Text>
  </View>
);

const ManagementReportPDF = ({ charts = [], parameters = {}, tenant = {} }) => {
  const logo = tenant?.documentLogo?.downloadUrl || tenant?.icon?.downloadUrl;

  return (
    <Document title={`Management Report - ${parameters.reportName || ''}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.heading}>
          <View>
            <Text style={styles.title}>Management Report</Text>
            <Text style={styles.reportDate}>Report Date:  {parameters.reportDate}</Text>
          </View>
          <View style={styles.brand}>
            {!!logo && <Image src={logo} style={styles.logo} />}
            <Text style={styles.businessName}>{tenant?.businessName || ''}</Text>
          </View>
        </View>

        <View style={styles.parameters}>
          <View style={styles.parameterColumn}>
            <Parameter label="Report:" value={parameters.reportName} />
            <Parameter label="From:" value={parameters.from} />
            <Parameter label="To:" value={parameters.to} />
          </View>
          <View style={styles.parameterColumn}>
            <Parameter label="Preset Range:" value={parameters.presetRange} />
            <Parameter label="Compare To:" value={parameters.compareTo} />
          </View>
        </View>

        <View style={styles.charts}>
          {charts.map((chart, index) => (
            <View
              key={`${chart.title}-${index}`}
              style={[
                styles.chartCard,
                chart.fullWidth && styles.chartCardFull,
                { backgroundColor: chart.backgroundColor || '#FFFFFF' },
              ]}
              wrap={false}
            >
              <Text style={[styles.chartTitle, { color: chart.titleColor || '#202124' }]}>{chart.title}</Text>
              <Text style={[styles.chartSubtitle, { color: chart.subtitleColor || '#666666' }]}>{chart.subtitle}</Text>
              <Image src={chart.image} style={styles.chartImage} />
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default ManagementReportPDF;
